import React, { useState } from 'react';
import { Trophy, Edit3, TrendingUp, TrendingDown, Minus, X } from 'lucide-react';
import {
  computeSeasonProgress,
  SEASON_TARGET_TIERS,
  SEASON_TARGET_RANKS
} from '../../lib/seasonObjective';

/**
 * APEX v7.7.3 — SeasonObjectiveCard + modale
 *
 * Props :
 *   - seasonObjective : { target_tier, target_rank, target_lp, description } | null
 *   - userSettings
 *   - rankSnapshots
 *   - onSave(objective) : persiste dans user_settings.season_objective
 */
export default function SeasonObjectiveCard({ seasonObjective, userSettings, rankSnapshots, onSave }) {
  const [showModal, setShowModal] = useState(false);

  if (!seasonObjective) {
    return (
      <>
        <div className="border border-dashed border-zinc-800 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy size={14} className="text-zinc-600" />
            <span className="text-xs text-zinc-500">Pas d'objectif de saison défini</span>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="text-[10px] uppercase tracking-wider font-bold text-amber-400 hover:text-amber-300 transition"
          >
            Définir un objectif
          </button>
        </div>
        {showModal && (
          <SeasonObjectiveModal
            current={null}
            onClose={() => setShowModal(false)}
            onSave={(obj) => { onSave(obj); setShowModal(false); }}
          />
        )}
      </>
    );
  }

  const progress = computeSeasonProgress(seasonObjective, userSettings, rankSnapshots);
  if (!progress) return null;

  const proj = progress.projection;

  return (
    <>
      <div className="border border-amber-400/40 bg-gradient-to-r from-amber-500/5 to-transparent overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/60">
          <div className="flex items-center gap-2">
            <Trophy size={14} className="text-amber-400" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-amber-400">
              Objectif de saison 2026
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 hover:text-zinc-300 transition flex items-center gap-1"
          >
            <Edit3 size={11} /> Modifier
          </button>
        </div>

        <div className="px-4 py-3 space-y-2">
          <div className="flex items-baseline justify-between">
            <div className="text-sm font-bold text-white">
              {seasonObjective.description || progress.targetLabel}
            </div>
            {progress.achieved && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">
                Atteint ✓
              </span>
            )}
          </div>

          <div className="h-2 bg-zinc-900 border border-zinc-800 overflow-hidden">
            <div
              className="h-full bg-amber-400 transition-all duration-700"
              style={{ width: `${progress.percent}%`, boxShadow: '0 0 8px rgba(251,191,36,0.4)' }}
            />
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500">
            <span>{progress.currentLabel}</span>
            <span className="text-amber-400 font-bold">{progress.percent}%</span>
            <span>{progress.targetLabel}</span>
          </div>

          {proj?.available && (
            <div className="text-[11px] text-zinc-400 pt-1.5 border-t border-zinc-800/40 mt-2 flex items-start gap-1.5">
              {proj.trend === 'up' && <TrendingUp size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" />}
              {proj.trend === 'down' && <TrendingDown size={12} className="text-rose-400 mt-0.5 flex-shrink-0" />}
              {proj.trend === 'flat' && <Minus size={12} className="text-zinc-500 mt-0.5 flex-shrink-0" />}
              <span>
                Projection : à ton rythme ({proj.lpPerDay >= 0 ? '+' : ''}{proj.lpPerDay} LP/j),
                fin de saison ≈ <span className="text-white font-bold">{proj.projectedLabel}</span>
                {progress.gapLp > 0 && !progress.achieved && !progress.isApexTarget && (
                  <> · il te manque <span className="text-amber-400 font-bold">+{progress.gapLp} LP</span></>
                )}
                {progress.isApexTarget && !progress.achieved && (
                  <> · objectif <span className="text-amber-400 font-bold">{progress.targetLabel}</span> (dépend du ladder)</>
                )}
              </span>
            </div>
          )}
          {proj && !proj.available && (
            <div className="text-[10px] text-zinc-600 italic pt-1.5 border-t border-zinc-800/40 mt-2">
              Projection indisponible : {proj.reason}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <SeasonObjectiveModal
          current={seasonObjective}
          onClose={() => setShowModal(false)}
          onSave={(obj) => { onSave(obj); setShowModal(false); }}
        />
      )}
    </>
  );
}

export function SeasonObjectiveModal({ current, onClose, onSave }) {
  const [tier, setTier] = useState(current?.target_tier || 'DIAMOND');
  const [rank, setRank] = useState(current?.target_rank || 'IV');
  const [description, setDescription] = useState(current?.description || '');

  const isApex = tier === 'MASTER' || tier === 'GRANDMASTER' || tier === 'CHALLENGER';

  const handleSave = () => {
    const cap = (s) => s.charAt(0) + s.slice(1).toLowerCase();
    const autoDesc = isApex
      ? `Atteindre ${cap(tier)} cette saison`
      : `Atteindre ${cap(tier)} ${rank} minimum`;
    onSave({
      season: '2026',
      target_tier: tier,
      target_rank: isApex ? null : rank,
      target_lp: isApex ? 0 : null,
      description: description.trim() || autoDesc,
      created_at: current?.created_at || new Date().toISOString(),
      achieved_at: null
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-zinc-950 border border-zinc-800 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-amber-400" />
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-amber-400">
              Objectif de saison
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
              Tier cible
            </div>
            <div className="grid grid-cols-3 gap-2">
              {SEASON_TARGET_TIERS.map(t => (
                <button
                  key={t}
                  onClick={() => setTier(t)}
                  className={`px-2 py-1.5 text-[11px] font-bold border transition ${
                    tier === t
                      ? 'border-amber-400 text-amber-400 bg-amber-400/5'
                      : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  {t.charAt(0) + t.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          {!isApex && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
                Division minimum
              </div>
              <div className="grid grid-cols-4 gap-2">
                {SEASON_TARGET_RANKS.map(r => (
                  <button
                    key={r}
                    onClick={() => setRank(r)}
                    className={`px-2 py-1.5 text-xs font-bold border transition ${
                      rank === r
                        ? 'border-amber-400 text-amber-400 bg-amber-400/5'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
              Description (optionnel)
            </div>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex : Atteindre Master cette saison"
              maxLength={80}
              className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-sm text-white placeholder:text-zinc-600 focus:border-amber-400 outline-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold text-zinc-400 hover:text-zinc-200 transition"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 border-2 border-amber-400 text-amber-400 hover:bg-amber-400/10 font-bold text-xs uppercase tracking-wider transition"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
